import { IConflictDto } from "@kuika/kuika-cl-model/src/dtos/branch.dto";
import React from "react";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import { BranchChangeCaption } from "./branch-change-captions";
import { generateWorkflowCaptions, workflowChangedNodeIds } from "./branch-workflow-captions";
import { WorkflowBpmnDiff } from "./workflow-bpmn-diff";
import "./workflow-conflict-view.scss";

function parseModel(snapshot: string | undefined): unknown {
  if (!snapshot) return null;
  try {
    return JSON.parse(snapshot);
  } catch {
    return null;
  }
}

function xmlString(value: string | undefined): string | undefined {
  return value && value.trim() ? value : undefined;
}

/** The BPMN xml lives inside the workflow model snapshot (Xml/xml field), not a separate region. */
function xmlFromModel(model: unknown): string | undefined {
  if (!model || typeof model !== "object") return undefined;
  const record: Record<string, unknown> = model as Record<string, unknown>;
  const value: unknown = record.xml ?? record.Xml;
  return typeof value === "string" ? xmlString(value) : undefined;
}

function ChangeList({ title, captions }: { title: string; captions: BranchChangeCaption[] }): React.ReactElement {
  return (
    <div className="wf-conflict__changes">
      <div className="wf-conflict__changes-title">{title}</div>
      {captions.length === 0 ? (
        <div className="wf-conflict__changes-empty">No changes vs. the common base</div>
      ) : (
        <ul className="wf-conflict__changes-list">
          {captions.map((c) => (
            <li key={c.id} className="wf-conflict__changes-item">
              <span className="wf-conflict__changes-dot" data-kind={c.kind} aria-hidden="true" />
              <span>{c.caption}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export interface WorkflowConflictViewProps {
  fileConflict: IConflictDto;
  /** Branch names. Target = "ours", Source = "theirs" (matches the rest of the merge UI). */
  sourceLabel: string;
  targetLabel: string;
  resolved: boolean;
  /** "ours" (target) | "theirs" (source) | other, when resolved. */
  resolutionStrategy?: string;
  onUseSource: () => void;
  onUseTarget: () => void;
}

/**
 * Conflict resolution for a workflow (a redundant-xml entity). A WF can't be merged granularly —
 * json and xml are two encodings of the same diagram, and there's no faithful graph-merge — so the
 * correct, non-corrupting resolution is to pick one whole side. This makes that choice INFORMED:
 * the two diagrams are shown side by side, and each side's changes (vs. the common base) are listed,
 * so the user sees exactly what each version contains before picking. Picking takes json + xml from
 * the same side, so they can never diverge.
 */
export function WorkflowConflictView({
  fileConflict,
  sourceLabel,
  targetLabel,
  resolved,
  resolutionStrategy,
  onUseSource,
  onUseTarget
}: WorkflowConflictViewProps): React.ReactElement {
  const regions = fileConflict.conflicts ?? [];

  // A single whole-entity region carries the full before/ours/theirs model snapshots; the BPMN xml
  // is a field inside each model, so both the diagrams and the change lists come from the snapshots.
  const snapRegion = regions.find((r) => r.oursSnapshot || r.theirsSnapshot || r.baseSnapshot) ?? regions[0];
  const baseModel: unknown = parseModel(snapRegion?.baseSnapshot ?? snapRegion?.base);
  const targetModel: unknown = parseModel(snapRegion?.oursSnapshot ?? snapRegion?.ours);
  const sourceModel: unknown = parseModel(snapRegion?.theirsSnapshot ?? snapRegion?.theirs);

  const targetXml: string | undefined = xmlFromModel(targetModel);
  const sourceXml: string | undefined = xmlFromModel(sourceModel);

  const targetChanges: BranchChangeCaption[] = generateWorkflowCaptions(baseModel, targetModel);
  const sourceChanges: BranchChangeCaption[] = generateWorkflowCaptions(baseModel, sourceModel);
  const changedNodeIds: string[] = workflowChangedNodeIds(sourceModel, targetModel);

  const usingTarget: boolean = resolved && resolutionStrategy === "ours";
  const usingSource: boolean = resolved && resolutionStrategy === "theirs";

  return (
    <div className="wf-conflict">
      <div className="wf-conflict__note">
        <KuikaSvg name="git-merge" width={15} height={15} />
        <span>
          This workflow changed on both branches. A diagram can’t be merged piece-by-piece, so choose which version to
          keep — the diagrams and change lists below show exactly what each side contains.
        </span>
      </div>

      <WorkflowBpmnDiff
        beforeXml={sourceXml}
        afterXml={targetXml}
        changedNodeIds={changedNodeIds}
        beforeLabel={`Source — ${sourceLabel}`}
        afterLabel={`Target — ${targetLabel}`}
      />

      <div className="wf-conflict__sides">
        <ChangeList title={`Source changes (${sourceLabel})`} captions={sourceChanges} />
        <ChangeList title={`Target changes (${targetLabel})`} captions={targetChanges} />
      </div>

      <div className="wf-conflict__actions">
        <KuikaButton type={usingSource ? "primary" : "secondary"} size="md" onClick={onUseSource}>
          <span className="wf-conflict__btn-label">
            {usingSource && <KuikaSvg name="check" width={14} height={14} />}
            {usingSource ? "Using source" : "Use source"} ({sourceLabel})
          </span>
        </KuikaButton>
        <KuikaButton type={usingTarget ? "primary" : "secondary"} size="md" onClick={onUseTarget}>
          <span className="wf-conflict__btn-label">
            {usingTarget && <KuikaSvg name="check" width={14} height={14} />}
            {usingTarget ? "Using target" : "Use target"} ({targetLabel})
          </span>
        </KuikaButton>
      </div>
    </div>
  );
}
